import { useMemo, useState } from 'react';
import DataTable from '../../../../reusable/DataTable/DataTable';
import CopyButton from '../../../../reusable/CopyButton/CopyButton';
import { useGetCommissionPaymentsQuery } from '../../../../store/api/affiliateApi';
import { debounce } from '../../../../utilities/debounce';
import { usd, NA } from '../../../../utilities/format';
import { PAGE_SIZE_OPTIONS, SEARCH_DELAY, formatDateTime, percent, signupUrl } from '../format';
import { t } from '../../../../i18n/i18n';

const columns = [
  { key: 'created_at', header: 'Date', width: 16, value: (r) => formatDateTime(r.created_at) },
  {
    key: 'commission_percentage',
    header: 'Commission %',
    width: 12,
    value: (r) => percent(r.commission_percentage, 2),
  },
  {
    key: 'amount',
    header: 'Amount',
    width: 12,
    value: (r) => usd(r.amount, { decimals: 2 }),
    tone: () => 'primary',
  },
  {
    key: 'affiliate_code',
    header: 'URL',
    width: 24,
    value: (r) => (r.affiliate_code ? signupUrl(r.affiliate_code) : NA),
    render: (r) => {
      if (!r.affiliate_code) return NA;
      const url = signupUrl(r.affiliate_code);
      return (
        <span className="aff-copy-cell">
          <a className="aff-link" href={url} target="_blank" rel="noopener noreferrer" title={url}>
            {url}
          </a>
          <CopyButton value={url} label="Affiliate link" />
        </span>
      );
    },
  },
];

/**
 * Affiliate Marketing — Commission Payouts. Live's Commission Payment tab
 * (`CommissionPayment.js` on main): every commission paid on a code, from
 * `/affiliate/v2/commission/payment/`, searched by URL and paged on the server.
 */
export default function CommissionPayouts() {
  const [params, setParams] = useState({ page: 1, page_size: PAGE_SIZE_OPTIONS[0], search: '' });

  const onSearch = useMemo(
    () => debounce((value) => setParams((prev) => ({ ...prev, search: value.trim(), page: 1 })), SEARCH_DELAY),
    []
  );

  const { data, isFetching, isError } = useGetCommissionPaymentsQuery({
    page: params.page,
    page_size: params.page_size,
    ...(params.search && { search: params.search }),
  });

  const rows = data?.results ?? [];
  const total = data?.count ?? rows.length;

  return (
    <DataTable
      icon="affiliate-revenue"
      title="Commission Payouts"
      count={isFetching && !data ? '—' : `${total}`}
      columns={columns}
      rows={rows}
      getRowId={(r, i) => r.id ?? `${r.created_at}-${i}`}
      minWidth={760}
      total={total}
      page={params.page}
      onPageChange={(page, size) => setParams((prev) => ({ ...prev, page, page_size: size ?? prev.page_size }))}
      pageSize={params.page_size}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      loading={isFetching}
      searchPlaceholder="Search by URL"
      onSearch={onSearch}
      emptyMessage={
        isError
          ? t('Could not load your commission payouts')
          : params.search
            ? t('No commission payouts match “{{search}}”', { search: params.search })
            : t('No commission payouts yet')
      }
    />
  );
}
